/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */

/**
 * Utility methods to capture GraphQl errors and expose them to the frontend for reporting.
 * ```
 * Sample usage:
 * const id = startErrorCapture();
 * ...
 * const errors = stopErrorCapture(id);
 * ```
 */
import type { PayloadError } from 'relay-runtime';
import { v4 as uuid } from 'uuid';
import { autoBeforeEachTest } from '@jira/platform__utils-auto-before-each-test/src/auto-before-each-test.tsx';

export type ErrorMeta = {
	operationName: string;
	requestId?: string;
};

interface PayloadExtensions {
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	[key: string]: any;
}

export type AGGError = PayloadError & {
	path?: string[];
	extensions?: PayloadExtensions;
};

export type ErrorWrapper = {
	errors: AGGError[];
	meta: ErrorMeta;
};

const errorCollector = new Map<string, ErrorWrapper[]>();
autoBeforeEachTest(() => {
	errorCollector.clear();
});

export const CLEAN_UP_TIMEOUT: number = 120 * 1000; // 2min

const scheduleCleanup = (key: string) => {
	setTimeout(() => {
		errorCollector.delete(key);
	}, CLEAN_UP_TIMEOUT);
};

export const startCaptureGraphQlErrors = (key: string = uuid()): string => {
	errorCollector.set(key, []);
	scheduleCleanup(key);
	return key;
};

export const stopCaptureGraphQlErrors = (key: string): ErrorWrapper[] => {
	const errors = errorCollector.get(key);
	errorCollector.delete(key);
	return errors || [];
};

export const captureErrors = (errors: AGGError[] | null, meta: ErrorMeta): void => {
	if (errors) {
		errorCollector.forEach((value, key) => {
			errorCollector.set(key, [...value, { errors, meta }]);
		});
	}
};

export const isUnauthenticated = (errors: AGGError[]): boolean => {
	if (errors.length > 0) {
		return errors.some((error) => error.message.startsWith('Auth category: UNAUTHENTICATED'));
	}
	return false;
};
